import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { Acknowledge, Container, Stack } from '../../components'
import { mutations } from '../../graphql'
import { useAuth } from '../../hooks'
import {
  AcceptSuccess,
  AcceptError,
  AcceptLoading,
  AdvisorDetailsForm,
  useAdvisorAcceptanceGate,
} from './advisor-connection-shared'

/**
 * FlowId-keyed sibling of AcceptConnection, for advisors already logged in
 * when they accept (e.g. via the sign-in popup) - the OnboardingFlow token
 * is hashed in the DB and can't be recovered for a URL, so this reuses the
 * same name/custodian-details guard against the flow ID instead.
 */
export function AcceptConnectionById() {
  const { flowId } = useParams()
  const [{ isLoggedIn }] = useAuth()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState(null)
  const [nameSaved, setNameSaved] = useState(false)
  const hasCalledRef = useRef(false)

  const {
    me,
    loading: meLoading,
    formRequired,
    advisorFundProfileId,
    existingSettings,
    suggestedCustodianName,
  } = useAdvisorAcceptanceGate({ skip: !isLoggedIn })

  const [acceptConnection, { data, error }] = useMutation(mutations.onboarding.acceptAdvisorConnectionById, {
    onError: (_error) => {
      setErrorMessage(_error.message)
    },
  })

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { state: { afterLogin: `/advisor/accept-by-id/${flowId}` } })
      return
    }

    if (meLoading) return
    if (formRequired && !advisorFundProfileId) return
    if (formRequired && !nameSaved) return

    if (hasCalledRef.current) return
    hasCalledRef.current = true

    acceptConnection({ variables: { flowId: Number(flowId) } })
  }, [isLoggedIn, flowId, navigate, meLoading, formRequired, advisorFundProfileId, nameSaved, acceptConnection])

  if (!isLoggedIn) return null

  if (meLoading) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AcceptLoading />
        </Stack>
      </Container>
    )
  }

  if (formRequired && !advisorFundProfileId) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AcceptLoading />
        </Stack>
      </Container>
    )
  }

  if (formRequired && !nameSaved) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AdvisorDetailsForm
            me={me}
            existingSettings={existingSettings}
            suggestedCustodianName={suggestedCustodianName}
            advisorFundProfileId={advisorFundProfileId}
            onSave={() => setNameSaved(true)}
          />
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <Stack justifyItems="center">
        <Acknowledge.Switch
          success={!!data && <AcceptSuccess />}
          error={!!error && <AcceptError message={errorMessage} />}
        >
          <AcceptLoading />
        </Acknowledge.Switch>
      </Stack>
    </Container>
  )
}
